import React, { useState } from 'react';
import { FiUser, FiLock, FiEye, FiEyeOff, FiMail, FiPhone, FiCheckCircle, FiUsers, FiTrendingUp, FiAward, FiAlertTriangle } from 'react-icons/fi';
import { useAuth } from '../../provider/auth.provider';
import api from '../../libs/axios';

const WEB_DEVICE_STORAGE_KEY = 'eminence_web_device_id';

const createWebDeviceId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return `web-${window.crypto.randomUUID()}`;
  }

  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const getWebDeviceId = () => {
  if (typeof window === 'undefined') {
    return createWebDeviceId();
  }

  const existingDeviceId = window.localStorage.getItem(WEB_DEVICE_STORAGE_KEY);
  if (existingDeviceId) {
    return existingDeviceId;
  }

  const nextDeviceId = createWebDeviceId();
  window.localStorage.setItem(WEB_DEVICE_STORAGE_KEY, nextDeviceId);
  return nextDeviceId;
};

const getBrowserName = () => {
  if (typeof navigator === 'undefined') {
    return 'Browser';
  }

  const userAgent = navigator.userAgent;

  if (userAgent.includes('Edg/')) return 'Microsoft Edge';
  if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) return 'Google Chrome';
  if (userAgent.includes('Firefox/')) return 'Mozilla Firefox';
  if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) return 'Safari';

  return 'Browser';
};

const getWebDeviceInfo = () => {
  const browserName = getBrowserName();
  const platform = typeof navigator !== 'undefined' ? navigator.platform || 'Web' : 'Web';

  return {
    name: `${browserName} on ${platform}`,
    type: 'web',
    version: browserName,
  };
};

// Forced password change modal shown when must_change_password or password_expired
const ForceChangePasswordModal: React.FC<{
  token: string;
  remember: boolean;
  onSuccess: (token: string, remember: boolean) => void;
}> = ({ token, remember, onSuccess }) => {
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPwd !== confirmPwd) { setError('New passwords do not match'); return; }
    if (newPwd.length < 8) { setError('Password must be at least 8 characters'); return; }
    setSubmitting(true);
    try {
      await api.put('/auth/change-password',
        { current_password: currentPwd, new_password: newPwd },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess(token, remember);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <FiAlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Password Change Required</h2>
            <p className="text-sm text-gray-500">You must set a new password before continuing.</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        <form onSubmit={handleChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPwd}
                onChange={e => setCurrentPwd(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                placeholder="Enter current password"
              />
              <button type="button" onClick={() => setShowCurrent(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showCurrent ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPwd}
                onChange={e => setNewPwd(e.target.value)}
                required minLength={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                placeholder="Min 8 characters"
              />
              <button type="button" onClick={() => setShowNew(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showNew ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPwd}
              onChange={e => setConfirmPwd(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Repeat new password"
            />
          </div>
          <ul className="text-xs text-gray-500 space-y-1 mt-1">
            <li>• Must be at least 8 characters</li>
            <li>• Cannot match your current or previous password</li>
            <li>• Will expire after 90 days</li>
          </ul>
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-blue-800 text-white rounded-lg font-semibold text-sm hover:bg-blue-900 disabled:opacity-60 transition-colors">
            {submitting ? 'Changing Password…' : 'Change Password & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

const LoginPage = () => {
  const [identifier, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Force change modal state
  const [forceChangeToken, setForceChangeToken] = useState<string | null>(null);
  const { login } = useAuth();

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const deviceInfo = getWebDeviceInfo();
      const res = await api.post('/auth/login', {
        identifier,
        password,
        remember_me: remember,
        device_address: getWebDeviceId(),
        device_name: deviceInfo.name,
        device_type: deviceInfo.type,
        device_info: deviceInfo,
      });
      const data = res?.data?.data;
      const token = data?.access_token;

      if (data?.password_change_required) {
        // Block access until password is changed
        setForceChangeToken(token);
        return;
      }

      await login(token, remember);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Login failed';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-y-12"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 py-20 text-white">
          <div className="mb-12">
            <h2 className="text-4xl font-bold mb-6 leading-tight">
              Leading Management
              <span className="block text-blue-200">Consulting Excellence</span>
            </h2>
            <p className="text-xl text-blue-100 leading-relaxed">
              Providing comprehensive audit, survey and research services to drive your business forward.
            </p>
          </div>

          <div className="space-y-6 mb-12">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-700 rounded flex items-center justify-center">
                <FiAward className="h-6 w-6 text-blue-200" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Expert Audit Services</h3>
                <p className="text-blue-200">Comprehensive financial and operational auditing solutions</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-700 rounded flex items-center justify-center">
                <FiTrendingUp className="h-6 w-6 text-blue-200" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Research & Analytics</h3>
                <p className="text-blue-200">Data-driven insights for strategic decision making</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-700 rounded flex items-center justify-center">
                <FiUsers className="h-6 w-6 text-blue-200" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Survey Solutions</h3>
                <p className="text-blue-200">Professional survey design and implementation</p>
              </div>
            </div>
          </div>

          <div className="border-t border-blue-700 pt-8">
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center space-x-3 text-blue-200">
                <FiMail className="h-5 w-5" />
                <span className="font-medium">sales@espl.in</span>
              </div>
              <div className="flex items-center space-x-3 text-blue-200">
                <FiPhone className="h-5 w-5" />
                <span className="font-medium">+91 8810638216</span>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-10 right-10 w-32 h-32 bg-blue-700 rounded-full opacity-20 blur-xl"></div>
        <div className="absolute bottom-20 right-20 w-24 h-24 bg-indigo-600 rounded-full opacity-30 blur-lg"></div>
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          {/* Logo and Header */}
          <div className="flex justify-center items-center flex-col text-center mb-10">
            <div className="mb-6">
              <img src='/assets/images/logo.png' className='w-48 mb-2' />
              <p className="text-sm text-gray-500 font-medium">Management Consulting Firm</p>
            </div>
            <div className="border-l-4 border-blue-900 pl-4 mb-6">
              <p className="text-gray-700 text-lg font-semibold">Welcome Back</p>
              <p className="text-gray-500 text-sm">Sign in to access your dashboard</p>
            </div>
          </div>

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="identifier" className="block text-sm font-semibold text-gray-700 mb-2">
                Email/Username
              </label>
              <div className="relative group">
                <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 z-10 h-5 w-5 text-gray-400 group-focus-within:text-blue-900 transition-colors" />
                <input
                  id="identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-900/20 focus:border-blue-900 transition-all duration-200 bg-white"
                  placeholder="Enter your email address"
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative group">
                <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 z-10 h-5 w-5 text-gray-400 group-focus-within:text-blue-900 transition-colors" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-4 border-2 border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-900/20 focus:border-blue-900 transition-all duration-200 bg-white"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 h-5 w-5 text-gray-400 hover:text-blue-900 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 text-blue-900 focus:ring-blue-900 border-gray-300 rounded "
                />
                <label htmlFor="remember" className="ml-3 block text-sm font-medium text-gray-700">
                  Remember me
                </label>
              </div>
              <a href="#" className="text-sm text-blue-900 hover:text-blue-700 font-semibold transition-colors">
                Forgot password?
              </a>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded text-sm" role="alert">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-blue-900 to-blue-800 text-white py-4 px-6 rounded hover:from-blue-800 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-900/20 focus:ring-offset-2 transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
            >
              {submitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                  Signing In...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Need access?{' '}
              <a href="#" className="text-blue-900 hover:text-blue-700 font-semibold transition-colors">
                Contact Administrator
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Forced password change modal — blocks app access until done */}
      {forceChangeToken && (
        <ForceChangePasswordModal
          token={forceChangeToken}
          remember={remember}
          onSuccess={(token, rem) => {
            setForceChangeToken(null);
            login(token, rem);
          }}
        />
      )}
    </div>
  );
};

export default LoginPage;
